(() => {
  if (customElements.get('joffz-art-club')) return;

  class JoffzArtClub extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.dialog = this.querySelector('[data-ac-dialog]');
      this.launcher = this.querySelector('[data-ac-launch]');
      this.preview = this.dataset.preview === 'true';
      this.completedKey = 'joffz-art-club-completed-v1';
      this.nativeSuccess = this.querySelector('[data-ac-native-success]');
      this.nativeError = this.querySelector('[data-ac-native-error]');
      const submittedHere = location.hash === `#ArtClubForm-${this.dataset.sectionId}`;
      if ((this.nativeSuccess || this.nativeError) && !submittedHere) return;
      if (!this.dialog || !this.dialog.showModal || /(^|\/)(cart|checkouts?|account)(\/|$)/i.test(location.pathname)) return;
      if (!this.preview && this.hasCompleted()) {
        this.rememberCompleted();
        return;
      }
      if (!this.preview && this.dataset.subscriber === 'true') {
        this.rememberCompleted();
        if (!this.nativeSuccess) return;
      }

      this.controller = new AbortController();
      this.signal = this.controller.signal;
      this.cards = [...this.querySelectorAll('[data-ac-card]')];
      this.stages = [...this.querySelectorAll('[data-ac-stage]')];
      this.selected = 0;
      this.currentStage = 'cards';
      this.dataset.stage = 'cards';
      this.strings = JSON.parse(this.querySelector('[data-ac-strings]').textContent);
      this.storageKey = 'joffz-art-club-dismissed-v1';
      this.sessionKey = 'joffz-art-club-opened-v1';
      this.hiddenLegacy = [];
      this.launcher.hidden = false;

      this.listen(this.launcher, 'click', () => this.open());
      this.listen(this.querySelector('[data-ac-close]'), 'click', () => this.close());
      this.listen(this.dialog, 'cancel', event => {
        event.preventDefault();
        this.close();
      });
      this.listen(this.dialog, 'close', () => this.afterClose());
      this.listen(this.dialog, 'pointerdown', event => {
        const rect = this.dialog.getBoundingClientRect();
        this.backdropPress = event.target === this.dialog && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom);
      });
      this.listen(this.dialog, 'click', event => {
        if (event.target === this.dialog && this.backdropPress) this.close();
        this.backdropPress = false;
      });
      this.cards.forEach(card => this.listen(card, 'click', () => this.pick(card)));
      this.querySelectorAll('[data-ac-copy]').forEach(button => this.listen(button, 'click', async () => {
        try {
          await navigator.clipboard.writeText(button.dataset.acCopy);
          button.textContent = this.strings.copied;
          this.querySelector('[data-ac-announcement]').textContent = this.strings.copied;
        } catch (_) {
          this.querySelector('[data-ac-announcement]').textContent = this.strings.copyFailed;
          button.textContent = this.strings.copyFailed;
        }
      }));
      this.listen(this.querySelector('[data-ac-preview-submit]'), 'click', () => this.submitPreview());
      if (this.preview) {
        this.listen(this.querySelector('[data-ac-email]'), 'keydown', event => {
          if (event.key === 'Enter') {
            event.preventDefault();
            this.submitPreview();
          }
        });
        this.listen(this.querySelector('[data-ac-email]'), 'input', () => this.clearError());
        this.listen(this.querySelector('[data-ac-consent]'), 'change', () => this.clearError());
      }
      this.listen(document, 'click', event => {
        const link = event.target.closest('a[href]');
        if (!link || !link.hash || link.hash !== '#art-club') return;
        const url = new URL(link.href, location.href);
        if (url.origin !== location.origin || url.pathname !== location.pathname) return;
        event.preventDefault();
        this.open();
      });
      this.listen(window, 'hashchange', () => {
        if (location.hash === '#art-club') this.open();
      });
      this.listen(document, 'visitorConsentCollected', () => this.scheduleAutoOpen());
      this.listen(document, 'visibilitychange', () => this.scheduleAutoOpen());
      this.listen(window, 'storage', event => {
        if (!this.preview && event.key === this.completedKey && event.newValue === '1') {
          this.rememberCompleted();
          this.close();
        }
      });
      this.listen(window, 'pageshow', event => {
        if (!this.preview && event.persisted && this.hasCompleted()) {
          this.rememberCompleted();
          this.close();
        }
      });
      this.listen(document, 'shopify:section:unload', event => {
        if (event.detail?.sectionId === this.dataset.sectionId) this.teardown();
      });

      if (this.nativeSuccess) {
        if (!this.preview) this.rememberCompleted();
        const target = this.querySelector('[data-ac-native-result]');
        this.nativeSuccess.hidden = false;
        target.append(this.nativeSuccess);
        this.showStage('success');
        this.open();
      } else if (this.nativeError) {
        this.showStage('email');
        this.open();
        this.queueFocus(this.querySelector('[data-ac-email]'));
      } else if (location.hash === '#art-club' || (this.preview && new URLSearchParams(location.search).get('art_club') === 'preview')) {
        this.open();
      } else {
        this.scheduleAutoOpen();
      }
    }

    hasCompleted() {
      if (this.completed) return true;
      try { if (localStorage.getItem(this.completedKey) === '1') return true; } catch (_) {}
      try { return document.cookie.split(';').some(value => value.trim() === `${this.completedKey}=1`); } catch (_) { return false; }
    }

    rememberCompleted() {
      if (this.preview) return;
      this.completed = true;
      this.dataset.completed = 'true';
      if (this.launcher) this.launcher.hidden = true;
      clearTimeout(this.autoTimer);
      try { localStorage.setItem(this.completedKey, '1'); } catch (_) {}
      try { document.cookie = `${this.completedKey}=1; Path=/; Max-Age=31536000; SameSite=Lax; Secure`; } catch (_) {}
      this.dispatchEvent(new CustomEvent('joffz:art-club:completed', { bubbles: true }));
    }

    listen(target, name, handler) {
      target?.addEventListener(name, handler, { signal: this.signal });
    }

    pick(card) {
      if (this.selected >= 3 || card.classList.contains('is-picked')) return;
      const offer = this.querySelector(`[data-ac-offer="${this.selected}"]`).content.cloneNode(true);
      const back = card.querySelector('[data-ac-card-back]');
      back.append(offer);
      const label = [...back.children].map(node => node.textContent.trim()).filter(Boolean).join('. ');
      card.setAttribute('aria-label', this.strings.revealed.replace('__OFFER__', label));
      card.setAttribute('aria-pressed', 'true');
      card.classList.add('is-picked');
      card.disabled = true;
      this.selected += 1;
      this.querySelector('[data-ac-progress]').textContent = this.strings.progress.replace('__COUNT__', String(this.selected));
      this.querySelector('[data-ac-announcement]').textContent = this.strings.revealed.replace('__OFFER__', label);
      this.querySelectorAll('.jac__progress-marks i').forEach((mark, index) => mark.classList.toggle('is-filled', index < this.selected));
      if (this.selected === 3) {
        this.cards.forEach(button => { button.disabled = true; });
        this.querySelector('.jac__deck').classList.add('is-complete');
        clearTimeout(this.advanceTimer);
        this.advanceTimer = setTimeout(() => {
          if (this.dialog.open && this.currentStage === 'cards') this.showStage('email', true);
        }, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 150 : 650);
      } else {
        this.queueFocus(this.cards.find(button => !button.disabled));
      }
    }

    showStage(stage, focus = false) {
      this.currentStage = stage;
      this.dataset.stage = stage;
      this.stages.forEach(panel => { panel.hidden = panel.dataset.acStage !== stage; });
      const titleKey = stage === 'cards' ? 'cardsTitle' : stage === 'email' ? 'emailTitle' : this.preview ? 'previewSuccessTitle' : 'successTitle';
      const title = this.querySelector('[data-ac-title]');
      title.textContent = this.strings[titleKey];
      title.tabIndex = -1;
      this.querySelector('[data-ac-scroll]').scrollTop = 0;
      this.querySelector('[data-ac-announcement]').textContent = '';
      if (focus) this.queueFocus(title);
    }

    clearError() {
      const error = this.querySelector('[data-ac-error]');
      if (error) {
        error.hidden = true;
        error.textContent = '';
      }
      this.querySelector('[data-ac-email]')?.removeAttribute('aria-invalid');
      this.querySelector('[data-ac-consent]')?.removeAttribute('aria-invalid');
    }

    submitPreview() {
      if (!this.preview || this.selected !== 3 || this.currentStage !== 'email') return;
      const email = this.querySelector('[data-ac-email]');
      const consent = this.querySelector('[data-ac-consent]');
      const error = this.querySelector('[data-ac-error]');
      this.clearError();
      email.value = email.value.trim();
      const invalid = !email.checkValidity() ? email : !consent.checked ? consent : null;
      if (invalid) {
        error.textContent = invalid === email ? this.strings.emailError : this.strings.consentError;
        error.hidden = false;
        invalid.setAttribute('aria-invalid', 'true');
        invalid.focus({ preventScroll: false });
        return;
      }
      email.value = '';
      consent.checked = false;
      this.showStage('success', true);
    }

    queueFocus(target) {
      cancelAnimationFrame(this.focusFrame);
      this.focusFrame = requestAnimationFrame(() => {
        if (this.dialog.open && target?.isConnected) target.focus({ preventScroll: true });
      });
    }

    open(automatic = false) {
      if (!this.controller || this.dialog.open) return;
      if (!this.preview && this.hasCompleted() && this.currentStage !== 'success') return;
      if (automatic && (!this.canAutoOpen() || this.hasOpenOverlay())) return;
      clearTimeout(this.autoTimer);
      this.returnFocus = document.activeElement;
      this.scrollY = window.scrollY;
      this.bodyStyle = {};
      ['position', 'top', 'width', 'overflow', 'padding-right'].forEach(property => {
        this.bodyStyle[property] = { value: document.body.style.getPropertyValue(property), priority: document.body.style.getPropertyPriority(property) };
      });
      const scrollbar = Math.max(0, innerWidth - document.documentElement.clientWidth);
      const padding = parseFloat(getComputedStyle(document.body).paddingRight) || 0;
      this.htmlStyle = {};
      ['overflow', 'scrollbar-gutter'].forEach(property => {
        this.htmlStyle[property] = { value: document.documentElement.style.getPropertyValue(property), priority: document.documentElement.style.getPropertyPriority(property) };
      });
      document.documentElement.style.setProperty('overflow', 'hidden');
      document.documentElement.style.setProperty('scrollbar-gutter', 'auto');
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      if (scrollbar) document.body.style.paddingRight = `${padding + scrollbar}px`;
      try {
        this.dialog.showModal();
      } catch (_) {
        this.restoreBody();
        return;
      }
      this.suppressLegacy();
      this.launcher.setAttribute('aria-expanded', 'true');
      if (this.selected === 3 && this.currentStage === 'cards') this.showStage('email', true);
      if (!this.preview) {
        try { sessionStorage.setItem(this.sessionKey, '1'); } catch (_) {}
      }
      this.dispatchEvent(new CustomEvent('joffz:art-club:open', { bubbles: true, detail: { preview: this.preview } }));
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    afterClose() {
      cancelAnimationFrame(this.focusFrame);
      clearTimeout(this.advanceTimer);
      this.restoreBody();
      this.restoreLegacy();
      this.launcher.setAttribute('aria-expanded', 'false');
      if (this.preview) {
        const email = this.querySelector('[data-ac-email]');
        if (email) email.value = '';
        const consent = this.querySelector('[data-ac-consent]');
        if (consent) consent.checked = false;
        this.clearError();
      } else {
        try { localStorage.setItem(this.storageKey, String(Date.now())); } catch (_) {}
      }
      if (this.returnFocus?.isConnected && this.returnFocus !== document.body) this.returnFocus.focus({ preventScroll: true });
      this.dispatchEvent(new CustomEvent('joffz:art-club:close', { bubbles: true, detail: { preview: this.preview } }));
    }

    restoreBody() {
      if (!this.bodyStyle) return;
      Object.entries(this.bodyStyle).forEach(([property, previous]) => {
        if (previous.value) document.body.style.setProperty(property, previous.value, previous.priority);
        else document.body.style.removeProperty(property);
      });
      this.bodyStyle = null;
      Object.entries(this.htmlStyle || {}).forEach(([property, previous]) => {
        if (previous.value) document.documentElement.style.setProperty(property, previous.value, previous.priority);
        else document.documentElement.style.removeProperty(property);
      });
      this.htmlStyle = null;
      window.scrollTo({ top: this.scrollY, left: window.scrollX, behavior: 'instant' });
    }

    suppressLegacy() {
      const selectors = this.dataset.legacySelectors?.trim();
      if (!selectors) return;
      try {
        document.querySelectorAll(selectors).forEach(node => {
          if (node.contains(this) || this.contains(node)) return;
          this.hiddenLegacy.push({ node, value: node.style.getPropertyValue('display'), priority: node.style.getPropertyPriority('display') });
          node.style.setProperty('display', 'none', 'important');
        });
      } catch (_) {}
    }

    restoreLegacy() {
      (this.hiddenLegacy || []).forEach(({ node, value, priority }) => {
        if (value) node.style.setProperty('display', value, priority);
        else node.style.removeProperty('display');
      });
      this.hiddenLegacy = [];
    }

    canAutoOpen() {
      if (!this.preview && this.hasCompleted()) return false;
      if (this.dataset.autoOpen !== 'true' || document.visibilityState !== 'visible' || this.dialog.open) return false;
      try {
        if (sessionStorage.getItem(this.sessionKey)) return false;
        const dismissed = Number(localStorage.getItem(this.storageKey));
        if (dismissed && Date.now() - dismissed < 30 * 86400000) return false;
      } catch (_) { return false; }
      return true;
    }

    hasOpenOverlay() {
      return [...document.querySelectorAll('dialog[open], [aria-modal="true"], .popup-open')].some(node => node !== this.dialog && node.getClientRects().length && getComputedStyle(node).visibility !== 'hidden');
    }

    scheduleAutoOpen() {
      clearTimeout(this.autoTimer);
      if (!this.canAutoOpen()) return;
      const delay = Math.max(3, Number(this.dataset.delay) || 4);
      this.autoTimer = setTimeout(() => this.open(true), delay * 1000);
    }

    teardown() {
      clearTimeout(this.autoTimer);
      clearTimeout(this.advanceTimer);
      cancelAnimationFrame(this.focusFrame);
      if (this.dialog?.open) this.dialog.close();
      this.controller?.abort();
      this.restoreBody();
      this.restoreLegacy();
      this.controller = null;
    }

    disconnectedCallback() {
      this.teardown();
    }
  }

  customElements.define('joffz-art-club', JoffzArtClub);
})();
